import { z } from 'zod';

/**
 * KMD tegelik deklaratsioon client (Module 1.12 backcast). Talks to
 * `/api/kmd/actuals` and Zod-validates every payload at the boundary
 * (anti-slop — external data is never trusted). One row per declaration
 * month (`YYYY-MM`): the € figure the operator READ OFF the submitted EMTA
 * declaration — a recorded fact, never computed here (L9: integer cents;
 * the forecast twin and the variance both come from finance-core).
 */

export interface KmdActualsRequestOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works everywhere. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
}

const actualSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  actualCents: z.number().int(),
  note: z.string().nullable(),
  updatedAt: z.string(),
});
export type KmdActualDto = z.infer<typeof actualSchema>;

const listSchema = z.object({ actuals: z.array(actualSchema) });
const oneSchema = z.object({ actual: actualSchema });
const errorEnvelopeSchema = z.object({ error: z.object({ message: z.string() }) });

function authHeaders(accessToken?: string, extra: Record<string, string> = {}): Record<string, string> {
  return accessToken ? { ...extra, Authorization: `Bearer ${accessToken}` } : extra;
}

async function failureMessage(res: Response, fallback: string): Promise<string> {
  const body: unknown = await res.json().catch(() => null);
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.message : fallback;
}

/** All recorded actual declarations, keyed by month in the returned list. */
export async function fetchKmdActuals(
  options: KmdActualsRequestOptions = {},
): Promise<KmdActualDto[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/actuals`, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(`KMD actuals request failed: ${res.status}`);
  }
  return listSchema.parse(await res.json()).actuals;
}

/** Record (or correct) one month's declared figure — an upsert on the month. */
export async function saveKmdActual(
  month: string,
  actualCents: number,
  options: KmdActualsRequestOptions = {},
): Promise<KmdActualDto> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/actuals/${month}`, {
    method: 'PUT',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ actualCents }),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `KMD actual save failed: ${res.status}`));
  }
  return oneSchema.parse(await res.json()).actual;
}

/** Remove a month's recorded figure — the cell falls back to the backcast. */
export async function deleteKmdActual(
  month: string,
  options: KmdActualsRequestOptions = {},
): Promise<void> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/kmd/actuals/${month}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `KMD actual delete failed: ${res.status}`));
  }
}
